/**
 * @file Download Button Component.
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useApolloClient } from 'react-apollo';

import Button from './Button';
import { PhotoDownloadURL } from '../../graphql/queries/photos';
import { IMAGE_SIZE_CODE } from '../../constants/image-size-codes';

const propTypes = {
  photoId: PropTypes.string.isRequired,
  photoSize: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)).isRequired,
  includeWatermark: PropTypes.bool,
  downloadOriginal: PropTypes.bool,
  photoForegroundsOnly: PropTypes.bool,
  client: PropTypes.shape({
    query: PropTypes.func.isRequired,
  }).isRequired,
  children: PropTypes.node,
  onDownload: PropTypes.func,
  className: PropTypes.string,
};

const DownloadPhotoButton = ({
  photoId,
  photoSize,
  includeWatermark,
  downloadOriginal,
  downloadPhotoForeground,
  children,
  onDownload,
  className,
  ...props
}) => {
  const buttonRef = useRef();
  const [loading, setLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [error, setError] = useState(null);
  const client = useApolloClient();

  useEffect(() => {
    setLoading(false);
    setDownloadUrl(null);
  }, [photoId, includeWatermark, downloadOriginal, downloadPhotoForeground]);

  useEffect(() => {
    if (downloadUrl) {
      buttonRef?.current?.click?.();
      onDownload?.();
    }
  }, [downloadUrl]);

  const fetchDownloadLink = async () => {
    if (loading) {
      return;
    }

    setLoading(true);
    try {
      const {
        data: {
          photoDownloadUrls: { photo_urls: photoUrls },
        },
      } = await client.query({
        query: PhotoDownloadURL,
        variables: {
          photos: [
            {
              photo_id: photoId,
              photo_size: photoSize.toUpperCase(),
              include_watermark: includeWatermark,
              download_original_photo: downloadOriginal,
              download_photo_foreground: downloadPhotoForeground,
            },
          ],
        },
        fetchPolicy: 'network-only',
      });
      const [newDownloadUrl] = photoUrls;

      setDownloadUrl(newDownloadUrl);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const child = error ? 'Error. Please try again later' : children;
  const classes = classNames({
    'is-loading': loading,
    'has-error': !!error,
    [className]: !!className,
  });

  return (
    <Button
      disabled={loading}
      onClick={!downloadUrl ? () => fetchDownloadLink() : undefined}
      href={downloadUrl || undefined}
      inputRef={buttonRef}
      {...props}
      className={classes}
    >
      {child}
    </Button>
  );
};

DownloadPhotoButton.propTypes = propTypes;

export default DownloadPhotoButton;
